import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import SurveyService from '~/api/services/survey'
import Layout from '~/components/Layout'
import TableComponent from '~/components/TableComponent'
import i18n from '~/components/i18n'
import { useDebounce } from 'use-lodash-debounce';
import { RouteTypes } from '~/routes/constants'
import SortingChart from './AnswerCharts/SortingChart'
import { FiltersWrapper } from '~/pages/Dairy/DairyFactory/ActivityLogs/style'
import { Divider, Dropdown, Input } from 'semantic-ui-react'
import { SurveyQuery } from '~/QueryHooks/Survey'
import SliderChart from './AnswerCharts/SliderChart'
import SelectChart from './AnswerCharts/SelectChart'
import StarsChart from './AnswerCharts/StarsChart'
import NumberChart from './AnswerCharts/NumberChart'
import { formatNumberWithoutFractions, handleErrors } from '~/utils/helpers'

const QuestionStatistics: React.FC = () => {
  const { t } = useTranslation()
  const { id, questionId } = useParams()
  const [isBusy, setIsBusy] = useState(false)
  const [questions, setQuestions] = useState([])
  const [farmerGroups, setFarmerGroups] = useState([])
  const [search, setSearch] = useState('')
  const debouncedSearchValue = useDebounce(search, 300)
  const [parentGroupId, setParentGroupId] = useState(null)
  const [dataFiltered, setDataFiltered] = useState({
    search: '',
    questionId: questionId,
    farmerGroupId: ''
  })
  const [selectedQuestionId, setSelectedQuestionId] = useState(questionId)
  const tableColumnHeaders = [
    {
      name: i18n.t('Supplier No.'),
      key: 'supplierNo'
    },
    {
      name: i18n.t('Supplier'),
      key: 'companyName'
    },
    {
      name: i18n.t('Answer'),
      key: 'answer',
      type: 'answerColumn'
    }
  ];

  const {
    isLoading: isBusyChartData,
    data: surveyChartData,
    refetch: fetchFilteredChartData,
    isFetching: isFetchingChartData
  } = SurveyQuery.getSurveysStatChartData(id, { ...dataFiltered })

  const {
    isLoading: isBusyTableData,
    data: surveyTableData,
    refetch: fetchFilteredTableData,
    isFetching: isFetchingTableData
  } = SurveyQuery.getSurveysStatTableData(id, { ...dataFiltered })

  useEffect(() => {
    fetchQuestionStatistics()
  }, [])

  useEffect(() => {
    if (dataFiltered?.questionId) {
      fetchFilteredChartData()
      fetchFilteredTableData()
      setSelectedQuestionId(dataFiltered.questionId)
    }
  }, [dataFiltered])

  useEffect(() => {
    setDataFiltered({ ...dataFiltered, search: debouncedSearchValue })
  }, [debouncedSearchValue])

  async function fetchQuestionStatistics() {
    setIsBusy(true);
    try {
      const responseGroups = await SurveyService.getSurveyFarmerGroups(id)
      const responseQuestions = await SurveyService.getSurveyQuestionStatisticsList(id)
      setQuestions(responseQuestions.data)
      setFarmerGroups(responseGroups.data)
      if (!selectedQuestionId) {
        setSelectedQuestionId(responseQuestions.data[0]?.id)
        setDataFiltered({ ...dataFiltered, questionId: responseQuestions.data[0]?.id })
      }
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false);
  }

  function renderColumns(type, value, row) {
    const questionSelected = questions.find(question => question.id === dataFiltered.questionId)
    switch (questionSelected?.type) {
      case 'slider':
      case 'stars':
        return <div>{formatNumberWithoutFractions(row.value)}</div>
      case 'number':
        return <div>{row.value}</div>
      default:
        return <div>{row.answer || ''}</div>
    }
  }

  function renderTable() {
    return <TableComponent
      tableColumnHeaders={tableColumnHeaders}
      animated
      isFetchingData={isFetchingTableData}
      rows={surveyTableData || []}
      removePagination
      renderColumn={renderColumns}
      total={surveyTableData?.length || 0}
    />
  }

  function renderChart() {
    const searchKey = parentGroupId ? parentGroupId : selectedQuestionId
    const question = questions.find(question => question?.id === searchKey)
    switch (question?.type) {
      case 'sorting':
        return <SortingChart
          data={surveyChartData?.answers || {}}
        />
      case 'slider':
        return <SliderChart
          data={surveyChartData?.answers || []}
        />
      case 'checkbox':
        return
      case 'radio':
        return <SelectChart
          data={surveyChartData?.answers || []}
        />
      case 'select':
        return <SelectChart
          data={surveyChartData?.answers || []}
        />
      case 'text':
        return
      case 'multitext':
        return
      case 'number':
        return <NumberChart
          data={surveyChartData?.answers || []}
        />
      case 'stars':
        return <StarsChart
          data={surveyChartData?.answers || []}
        />
      case 'multiselect':
        return <SelectChart
          data={surveyChartData?.answers || []}
        />
      case 'group':
        return <SelectChart
          data={surveyChartData?.answers || []}
        />
      default:
        return
    }
  }

  function renderGroupItems(question) {
    return <>
      <Dropdown.Header>{question?.question}</Dropdown.Header>
      <Divider />
      {question?.groupQuestions.map(groupQuestion => {
        return <Dropdown.Item
          onClick={() => {
            setParentGroupId(question.id)
            setDataFiltered({
              ...dataFiltered,
              questionId: groupQuestion.id as string
            })
          }}
          key={groupQuestion?.id}>
          {groupQuestion.question}
        </Dropdown.Item>
      })}
    </>
  }

  function renderItem(question) {
    return <Dropdown.Item
      value={question.id}
      key={question.id}
      onClick={() => {
        if (question.type !== 'group') {
          setParentGroupId(null)
        }
        setDataFiltered({
          ...dataFiltered,
          //@ts-ignore stupid typescript
          questionId: question.id
        })
      }}
    >
      {question.question}
    </Dropdown.Item>
  }

  function renderFilters() {
    const searchKey = parentGroupId ? parentGroupId : selectedQuestionId
    const question = questions.find(question => question?.id === searchKey)
    return <FiltersWrapper>
      <Input
        icon={{ name: 'search', circular: true, link: true }}
        placeholder={t('Search...')}
        value={search}
        onChange={(e, data) => setSearch(data.value)}
      />
      <Dropdown
        placeholder={t('Select farmer group')}
        value={dataFiltered.farmerGroupId}
        noResultsMessage={t('No results found....')}
        options={farmerGroups.map(farmerGroup => ({ value: farmerGroup?.id, text: farmerGroup?.name }))}
        onChange={(e, data) => setDataFiltered({
          ...dataFiltered,
          farmerGroupId: data.value as string
        })}
        clearable
        selection
        search
        selectOnBlur={false}
      />
      <Dropdown
        selection
        text={question?.question}
        noResultsMessage={t('No results found....')}
        style={{ minWidth: 250 }}
        selectOnBlur={false}
      >
        <Dropdown.Menu>
          {questions.map(question => {
            return question.type === 'group' ? renderGroupItems(question) : renderItem(question)
          })}
        </Dropdown.Menu>
      </Dropdown>
    </FiltersWrapper>
  }

  const loading = isBusy || isBusyChartData || isBusyTableData

  return <Layout
    isBusy={loading}
    segmented
    filters={renderFilters()}
    title={t('Question statistics')}
    close={RouteTypes.SURVEY_SHOW.replace(':id', id)}
  >
    {questions.length > 0 && surveyChartData && !isFetchingChartData && surveyTableData?.length > 0 && renderChart()}
    {renderTable()}
  </Layout>

}
export default QuestionStatistics